import React, { useMemo } from 'react';
import { IoMdInformationCircleOutline } from "react-icons/io";
import { Zap } from 'lucide-react';
import dayjs from "dayjs";
import energyIndicator from "../../assets/indicator-energy-use-intensity.svg";
import { IconTitle } from '@/components/ui/icon-title';

// Define the scale categories with their thresholds and proper colors
export const SCALE_CATEGORIES = [
  { name: 'REF', value: 370, color: '#800000' },      // Danger 50 - REF (poor efficiency)
  { name: 'BEC', value: 298, color: '#FE4C00' },      // Bright Orange - BEC (below standard)
  { name: 'HEPS', value: 266, color: '#EA8F28' },     // Orange 40 - HEPS (moderate efficiency)
  { name: 'ECON', value: 161, color: '#14B0BC' },     // Success 50 - ECON (good performance)
  { name: 'ZEB', value: 126, color: '#0E7EE4' },      // Blue 50 - ZEB (excellent efficiency)
  { name: 'ZERO', value: 0, color: '#0E7EE4' }        // Blue 50
];

const ENERGY_USE_INTENSITY = {
  TITLE: "Energy Use Intensity",
  TITLE_BADGE: "BEC standard",
  GAUGE_WIDTH: 267,
  GAUGE_HEIGHT: 20,
  GAUGE_INDICATOR: [0, 126, 161, 266, 298, 370],
  INDICATOR_SIZE: "28",
  UNIT: (
    <p>
      kWh/m<sup>2</sup> per year
    </p>
  ),
};

interface EnergyEfficiencyProps {
  value: number;
  gfa?: number;
  hideValue?: boolean;
  date?: string;
  yearToDateEnergy?: number;
}

// Helper function to calculate bar position
const useBarLocation = (value: number) => {
  return useMemo(() => {
    if (!value) return 0;
    let position;
    const indicatorLength = ENERGY_USE_INTENSITY.GAUGE_INDICATOR.length;
    const fraction = 100 / (indicatorLength - 1);
    for (let index = 1; index < indicatorLength; index++) {
      const current = ENERGY_USE_INTENSITY.GAUGE_INDICATOR[index];
      const prev = ENERGY_USE_INTENSITY.GAUGE_INDICATOR[index - 1];
      if (value <= current) {
        position = Math.round(
          ((value - prev) / (current - prev)) * fraction +
          fraction * (index - 1)
        );
        break;
      }
    }

    return Math.min(100, Math.max(position as number, 0));
  }, [value]);
};

// Helper function to get the category based on the value
function getCategory(value: number) {
  // Check from best performance (lowest EUI value) upwards
  if (value <= SCALE_CATEGORIES[5].value) { // <= ZERO (0)
    return SCALE_CATEGORIES[5];
  }
  if (value <= SCALE_CATEGORIES[4].value) { // <= ZEB
    return SCALE_CATEGORIES[4];
  }
  if (value <= SCALE_CATEGORIES[3].value) { // <= ECON
    return SCALE_CATEGORIES[3];
  }
  if (value <= SCALE_CATEGORIES[2].value) { // <= HEPS
    return SCALE_CATEGORIES[2];
  }
  if (value <= SCALE_CATEGORIES[1].value) { // <= BEC
    return SCALE_CATEGORIES[1];
  }
  // Default to REF (highest value)
  return SCALE_CATEGORIES[0];
}

// ScaleBar component to display the energy efficiency scale
export function ScaleBar({ value }: { value: number }) {
  const barLocation = useBarLocation(value);
  const labels = ["REF", "BEC", "HEPS", "ECON", "ZEB"];
  const EFFICIENCY_COLOR = [
    "#800000",    // Danger 50 - REF (poor efficiency)
    "#FE4C00",    // Bright Orange - BEC (below standard)
    "#EA8F28",    // Orange 40 - HEPS (moderate efficiency)
    "#14B0BC",    // Success 50 - ECON (good performance)
    "#0E7EE4",    // Blue 50 - ZEB (excellent efficiency)
  ];
  const num = [370, 298, 266, 161, 126, 0];

  return (
    <div className="w-full">
      {/* Scale Bar */}
      <div className="relative w-full mt-2">
        <div className="relative w-full flex gap-[2px] mb-[8px] justify-between">
          {labels.map((label, index) => (
            <div
              key={index}
              style={{
                backgroundColor: EFFICIENCY_COLOR[index],
                borderRadius: `${index === 0 ? "10px" : "0"} ${index === labels.length - 1 ? "10px" : "0"
                  } ${index === labels.length - 1 ? "10px" : "0"} ${index === 0 ? "10px" : "0"
                  } `,
              }}
              className="flex justify-center items-center flex-1"
            >
              <p className="text-[12px] font-bold text-white">{label}</p>
            </div>
          ))}
        </div>
        <div className="absolute w-full flex justify-between">
          <p className="text-[10px] text-[#788796]">{num[0]}</p>
          <p className="text-[10px] text-[#788796]">
            {num[num.length - 1]}
          </p>
        </div>
        <div
          className="relative flex justify-between"
          style={{
            left: `${50 / labels.length}%`,
            width: `${(1 - 1 / labels.length) * 100}%`,
          }}
        >
          {num.map((item, index) => {
            if (index === 0 || index === num.length - 1) return null;
            return (
              <p
                key={index}
                className="text-[10px] text-center flex-1 text-[#788796]"
              >
                {item}
              </p>
            );
          })}
        </div>
        {barLocation != undefined && (
          <img
            src={energyIndicator}
            alt={"energy indicator"}
            height={ENERGY_USE_INTENSITY.INDICATOR_SIZE}
            width={ENERGY_USE_INTENSITY.INDICATOR_SIZE}
            className={`absolute`}
            style={{
              right: `calc(${barLocation}% -
              ${Number(ENERGY_USE_INTENSITY.INDICATOR_SIZE) / 2}px
            )`,
              top: "-5px",
            }}
          />
        )}
      </div>
    </div>
  );
}

// Main component for displaying energy efficiency
export function EnergyUseIntensityCard({ value, gfa, hideValue = false, date, yearToDateEnergy }: EnergyEfficiencyProps) {
  const category = getCategory(value);

  return (
    <div className="bg-white rounded-[10px] shadow-card p-2 gap-1.5 border border-light-grey flex flex-col">
      <div className="flex justify-between items-center">
        <IconTitle icon={Zap} title="Energy Use Intensity" />
        <div className="flex items-center gap-2">
          {date && (
            <p className="text-[9px] text-gray-500">
              Since {dayjs(date).format("MMM D, YYYY")}
            </p>
          )}
          <div className="flex items-center justify-center group relative">
            <IoMdInformationCircleOutline className="cursor-default text-primary h-4 w-4" />
            <div className="absolute hidden group-hover:block bg-white border border-gray-200 rounded-md p-1.5 top-2 left-6 z-10 whitespace-nowrap shadow-lg">
              <p className="text-[10px] text-gray-600">
                Energy Use Intensity = Annual Energy Consumption / Area
              </p>
              <p className="text-[10px] text-gray-500 mt-0.5">
                Annual energy interpolated from YTD data
              </p>
              {yearToDateEnergy && (
                <p className="text-[10px] text-gray-600 mt-0.5">
                  YTD Energy: {(yearToDateEnergy / 1000).toLocaleString(undefined, { maximumFractionDigits: 0 })} MWh
                </p>
              )}
              {gfa && (
                <p className="text-[10px] text-gray-600 mt-0.5">
                  Based on {gfa.toLocaleString()} m² GFA
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-[8px]">
        <div className="grow flex flex-col item-start justify-between relative">
          <p className="text-[9px] text-gray-500 mb-1">BEC Standard • <span className="font-semibold">Retail & Wholesale</span></p>
          <ScaleBar value={value} />
        </div>
        <div className="flex justify-center items-center flex-col rounded-lg p-3 w-[80px] h-[80px] bg-[#F9FAFF] border border-[#EDEFF9]">
          <p className="text-lg text-[#052745] font-bold leading-tight tabular-nums">
            {value != null ? value.toLocaleString(undefined, {
              maximumFractionDigits: 0,
              minimumFractionDigits: 0,
            }) : '-'}
          </p>
          <p className="text-[8px] text-dark-grey text-center leading-tight mt-0.5">
            kWh/m² per year
          </p>
          <div className="mt-1 px-2 py-0.5 bg-white rounded-md border border-[#DBE4FF]">
            <p className="text-[8px] font-semibold text-[#065BA9] uppercase">
              {getCategory(value).name}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
